import React, { useEffect, useState } from "react";
import {
  Avatar,
  Button,
  Form,
  Input,
  Progress,
  UploadFile,
  message,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import Dragger from "antd/es/upload/Dragger";
import DaggerCard from "@/components/Cards/DaggerCard/DaggerCard";

import { RcFile, UploadProps } from "antd/es/upload";
import { toast } from "sonner";
import { customUploadRequest } from "@/util/axiosUpload";

interface BasicDetailsProps {
  onFormFinish?: (values: ICreateResponderCompanyDto) => void;
  initialValues?: any;
  onNext: () => void;
  onPrevious?: () => void;
  isLastStep?: boolean;
}
const BasicDetails = ({
  initialValues,
  onNext,
  onPrevious,
  isLastStep,
  onFormFinish,
}: BasicDetailsProps) => {
  const [form] = Form.useForm();
  const [isUploading, setIsUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  // Set initial values when they change
  useEffect(() => {
    if (initialValues) {
      // Set form fields
      form.setFieldsValue({
        companyName: initialValues.companyName,
        companyDescription: initialValues.companyDescription,
        companyImage: initialValues.companyImage,
      });

      // Set image URL if available
      if (initialValues.companyImage) {
        setImageUrl(initialValues.companyImage);

        // Add to fileList if not already there
        if (!fileList.some((file) => file.url === initialValues.companyImage)) {
          let fileName =
            initialValues.companyImage.split("/").pop() || "company-image.jpg";

          // Truncate filename if longer than 20 characters
          if (fileName.length > 20) {
            const extension = fileName.substring(fileName.lastIndexOf("."));
            fileName =
              fileName.substring(0, 20) + "qwertyuiopasdfg... " + extension;
          }

          setFileList([
            {
              uid: "-1",
              name: fileName,
              status: "done",
              url: initialValues.companyImage,
            },
          ]);
        }
      }
    }
  }, [initialValues, form]);

  // Get pageCount from UtilsStore
  const handleNext = () => {
    onNext();
  };

  useEffect(() => {
    const formFields = form.getFieldsValue;
    // console.log("formFields ", formFields)
  }, []);

  const onFinish = async (values: ICreateResponderCompanyDto) => {
    // console.log(values);
    toast.success("Saved successfully");
    if (onFormFinish) {
      onFormFinish(values);
    }

    form.resetFields();
    setFileList([]);
    setImageUrl("");
  };

  const beforeUpload = (file: RcFile) => {
    const isJpegOrPngOrGif = ["image/jpeg", "image/png", "image/gif"].includes(
      file.type,
    );
    const isLt2M = file.size / 1024 / 1024 < 10;
    if (!isJpegOrPngOrGif) {
      toast.error("You can only upload JPG/PNG/GIF file!");
      return false; // This prevents the file from being added to the list
    }
    if (!isLt2M) {
      toast.error("Image must smaller than 10MB!");
      return false; // This prevents the file from being added to the list
    }
    return isJpegOrPngOrGif && isLt2M;
  };

  const iconUploadProps: UploadProps = {
    name: "file",
    customRequest: customUploadRequest,
    multiple: false,
    progress: {
      strokeColor: {
        "0%": "#108ee9",
        "100%": "#87d068",
      },
      strokeWidth: 3,
      format: (percent: number | undefined) =>
        percent && `${parseFloat(percent.toFixed(2))}%`,
    },
    onChange(info) {
      const lastSuccessFulUpload = fileList;
      let currentFileList = [...info.fileList];

      // Truncate filename if longer than 20 characters for display
      currentFileList = currentFileList.map((file) => {
        if (file.name && file.name.length > 20) {
          const extension = file.name.substring(file.name.lastIndexOf('.'));
          return {
            ...file,
            name: file.name.substring(0, 20) + "qwertyuiopasdfg... " + extension,
          };
        }
        return file;
      });

      setFileList(currentFileList);
      const { status } = info.file;
      if (status === "uploading") {
        setIsUploading(true);
      } else if (status === "done") {
        setIsUploading(false);
        form.setFieldsValue({ companyImage: info.file.response.path });
        setImageUrl(info.file.response.path);
        setFileList(currentFileList);
        toast.success(`${info.file.name} file uploaded successfully.`);
      } else if (status === "error") {
        setIsUploading(false);
        toast.error(`${info.file.name} file upload failed.`);
        // Revert to the last successful upload if the current upload fails
        if (lastSuccessFulUpload) {
          setFileList(lastSuccessFulUpload);
        } else {
          setFileList([]); // If there was no successful upload before, clear the fileList
        }
      }
    },
    fileList: fileList,
  };

  return (
    <div className="flex w-full h-full">
      <Form
        form={form}
        initialValues={initialValues}
        onFinish={onFinish}
        layout="vertical"
      >
        <div className="flex flex-col w-full">
          <p className="text-sm">Step 1: Basic Company Details</p>
          <Progress
            steps={6}
            size={[80, 10]}
            strokeColor={["#1757A2"]}
            percent={10}
            showInfo={false}
            className="rounded-full"
            strokeLinecap="round"
            strokeWidth={1}
            style={{
              height: "6px",
              borderRadius: "999px",
            }}
          />
        </div>

        <div className="flex flex-col w-full mt-3">
          <p className="text-sm">Name of Responder Company</p>
          <Form.Item
            name="companyName"
            required
            rules={[
              {
                required: true,
                message: "Please enter a name for your company.",
              },
            ]}
          >
            <Input />
          </Form.Item>
        </div>
        <div className="flex flex-col w-full">
          <p className="text-sm">Description</p>
          <Form.Item
            name="companyDescription"
            required
            rules={[
              {
                required: true,
                message: "Please enter a description for your company.",
              },
            ]}
          >
            <TextArea rows={4} />
          </Form.Item>
        </div>
        <div className="flex flex-col w-full">
          <p className="text-sm">Logo</p>
          <Form.Item
            name="companyImage"
            rules={[
              { required: true, message: "Please upload a company icon!" },
              // Custom validator to check if a valid logo file has been uploaded
            ]}
            className="mb-5 col-12"
          >
            <div className="flex w-full gap-6 flx-row justify-content-start">
              {imageUrl.length > 0 ? (
                <Avatar
                  shape="square"
                  size={64}
                  alt="responder company icon"
                  src={imageUrl}
                />
              ) : (
                <Avatar shape="square" size={64} alt="responder company icon" />
              )}
              <Dragger
                {...iconUploadProps}
                beforeUpload={beforeUpload}
                className="w-full"
                style={{ background: "transparent" }}
                maxCount={1}
                fileList={fileList} // Use the local fileList state
                accept=".jpg,.jpeg,.png" // This allows only .jpg, .jpeg, and .png files
              >
                <DaggerCard />
              </Dragger>
            </div>
          </Form.Item>
        </div>
        <div className="flex w-full">
          <Form.Item className="w-12 pt-2 mb-1">
            <div className="flex w-full gap-2 col-12 justify-content-end align-content-center">
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                className="w-3 bg-[#1757a2] shadow-none"
              >
                Next
              </Button>
            </div>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};

export default BasicDetails;
